import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { useSnackbar } from 'notistack';
import { useRole } from '@/hooks/useRole';
import { useUserAccess } from '@/hooks/useUserAccess';
import { displayName } from '@/auth/userAccess';

/** A failed sign-in in words a person can act on. */
function signInMessage(error: unknown): string | null {
  const status = (error as { status?: number })?.status;
  const code = (error as { code?: string; errorCode?: string })?.errorCode ?? (error as { code?: string })?.code;
  // Closing the popup is not an error worth showing.
  if (code === 'user_cancelled' || code === 'popup_window_error') return null;
  if (status === 403) return "This account isn't set up for Camptain. Ask your school office.";
  return error instanceof Error && error.message ? error.message : 'Sign-in failed. Try again.';
}

/** The header's profile avatar — who's signed in, and sign in / sign out with the school account (B2C, MFA). */
export function ProfileMenu() {
  const { roleLabel, portal } = useRole();
  const { access, available, signInWithSchoolAccount, signOut } = useUserAccess();
  const { enqueueSnackbar } = useSnackbar();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);

  const signIn = async () => {
    setAnchor(null);
    setBusy(true);
    try {
      await signInWithSchoolAccount();
    } catch (error) {
      const message = signInMessage(error);
      if (message) enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const leave = async () => {
    setAnchor(null);
    await signOut();
  };

  return (
    <>
      <Tooltip title={access ? displayName(access) : 'Profile'}>
        <IconButton
          aria-label="Open profile menu"
          aria-haspopup="menu"
          aria-expanded={anchor ? 'true' : undefined}
          size="small"
          disabled={busy}
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          <Avatar sx={{ width: 32, height: 32 }}>{(access ? access.user.firstName : roleLabel)[0]}</Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        <Stack sx={{ px: 2, py: 1.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {access ? displayName(access) : 'Demo User'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {roleLabel}
            {access ? ` · ${access.activeOrganization.organizationName}` : ''}
          </Typography>
        </Stack>
        {available ? <Divider /> : null}
        {available && !access ? (
          <MenuItem onClick={() => void signIn()}>
            <ListItemIcon>
              <LoginRoundedIcon fontSize="small" />
            </ListItemIcon>
            Sign in with school account
          </MenuItem>
        ) : null}
        {access ? (
          <MenuItem component={RouterLink} to={`/${portal}/my-profile`} onClick={() => setAnchor(null)}>
            <ListItemIcon>
              <PersonOutlineOutlinedIcon fontSize="small" />
            </ListItemIcon>
            My profile
          </MenuItem>
        ) : null}
        {access ? (
          <MenuItem onClick={() => void leave()}>
            <ListItemIcon>
              <LogoutRoundedIcon fontSize="small" />
            </ListItemIcon>
            Sign out
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}
